import { Controller } from "@hotwired/stimulus"

// Generic deferred-apply multi-select typeahead: types into an input, picks
// suggestions, builds hidden inputs + chips, and waits for the surrounding
// form to submit. 
export default class extends Controller {
  static targets = ["input", "results", "chips", "hiddenInputs", "clearButton"]
  static values = {
    searchUrl: String,
    fieldName: String,
    resultKey: String,
    tab: String,
    emptyText: { type: String, default: "Sin resultados" }
  }

  connect() {
    this.timeout = null
    this.updateClearButton()
  }

  disconnect() {
    if (this.timeout) clearTimeout(this.timeout)
  }

  search() {
    if (this.timeout) clearTimeout(this.timeout)

    this.timeout = setTimeout(() => this.fetchResults(), 200)
  }

  async fetchResults() {
    const query = this.inputTarget.value.trim()
    if (query.length < 2) {
      this.clearResults()
      return
    }

    const url = new URL(this.searchUrlValue, window.location.origin)
    url.searchParams.set("q", query)
    this.appendScope(url)

    try {
      const response = await fetch(url, { headers: { Accept: "application/json" }, cache: "no-store" })
      if (!response.ok) throw new Error(`Search request failed with status ${response.status}`)

      const rows = await response.json()
      this.renderResults(Array.isArray(rows) ? rows : [])
    } catch (_error) {
      // A network hiccup or a non-JSON response should still surface the
      // empty state instead of leaving the picker silently unresponsive.
      this.renderResults([])
    }
  }

  // Scope suggestions to the date range currently set in the surrounding
  // form, plus whatever any sibling filter (e.g. referrers, when this is the
  // routes picker) already has selected — so the picker only ever offers
  // values that exist within the *whole* active filter set, and selecting
  // one can never produce a combination that returns nothing.
  appendScope(url) {
    const form = this.element.closest("form")
    if (!form) return

    const dateFrom = form.querySelector('[name="date_from"]')?.value
    const dateTo = form.querySelector('[name="date_to"]')?.value
    if (dateFrom) url.searchParams.set("date_from", dateFrom)
    if (dateTo) url.searchParams.set("date_to", dateTo)

    form.querySelectorAll('[data-controller~="filter-multi-select"]').forEach((element) => {
      if (element === this.element) return

      const fieldName = element.dataset.filterMultiSelectFieldNameValue
      if (!fieldName) return

      element.querySelectorAll(`input[name="${fieldName}[]"]`).forEach((input) => {
        url.searchParams.append(`${fieldName}[]`, input.value)
      })
    })
  }

  add(event) {
    event.preventDefault()

    const value = event.currentTarget.dataset.value
    if (!value || this.selectedValues().includes(value)) return

    this.chipsTarget.querySelector("[data-empty-state]")?.remove()
    this.hiddenInputsTarget.insertAdjacentHTML("beforeend", this.hiddenInput(value))
    this.chipsTarget.insertAdjacentHTML("beforeend", this.chip(value))
    this.inputTarget.value = ""
    this.clearResults()
    this.updateClearButton()
  }

  remove(event) {
    event.preventDefault()

    const value = event.currentTarget.dataset.value
    this.element.querySelectorAll(`[data-selected-value="${CSS.escape(value)}"]`).forEach((element) => element.remove())
    this.updateClearButton()
  }

  clearAll(event) {
    event.preventDefault()

    const url = new URL(window.location.href)
    this.deleteParamVariants(url, this.fieldNameValue)
    if (this.hasTabValue) url.searchParams.set("tab", this.tabValue)

    window.location.href = url.toString()
  }

  deleteParamVariants(url, baseName) {
    url.searchParams.delete(baseName)
    url.searchParams.delete(`${baseName}[]`)
  }

  updateClearButton() {
    if (!this.hasClearButtonTarget) return

    this.clearButtonTarget.classList.toggle("hidden", this.selectedValues().length === 0)
  }

  renderResults(rows) {
    if (rows.length === 0) {
      this.resultsTarget.innerHTML = `<div class="px-3 py-2 text-sm text-gray-500">${this.escapeHtml(this.emptyTextValue)}</div>`
      this.resultsTarget.classList.remove("hidden")
      return
    }

    const selected = this.selectedValues()
    this.resultsTarget.innerHTML = rows.map((row) => {
      const value = row[this.resultKeyValue]
      const disabled = selected.includes(value)
      const classes = disabled ? "text-gray-400 cursor-not-allowed" : "text-gray-700 hover:bg-brand-primary-subtle"

      return `
        <button type="button"
                class="block w-full px-3 py-2 text-left text-sm ${classes}"
                data-action="filter-multi-select#add"
                data-value="${this.escapeHtml(value)}"
                ${disabled ? "disabled" : ""}>
          ${this.escapeHtml(value)}
        </button>
      `
    }).join("")
    this.resultsTarget.classList.remove("hidden")
  }

  clearResults() {
    this.resultsTarget.innerHTML = ""
    this.resultsTarget.classList.add("hidden")
  }

  selectedValues() {
    return Array.from(this.hiddenInputsTarget.querySelectorAll(`input[name="${this.fieldNameValue}[]"]`)).map((input) => input.value)
  }

  hiddenInput(value) {
    return `<input type="hidden" name="${this.fieldNameValue}[]" value="${this.escapeHtml(value)}" data-selected-value="${this.escapeHtml(value)}">`
  }

  chip(value) {
    return `
      <button type="button"
              class="rounded-full bg-brand-primary-subtle px-3 py-1 text-xs font-medium text-brand-primary hover:bg-brand-primary-subtle"
              data-action="filter-multi-select#remove"
              data-value="${this.escapeHtml(value)}"
              data-selected-value="${this.escapeHtml(value)}">
        ${this.escapeHtml(value)} ×
      </button>
    `
  }

  escapeHtml(value) {
    return String(value)
      .replaceAll("&", "&amp;")
      .replaceAll("<", "&lt;")
      .replaceAll(">", "&gt;")
      .replaceAll('"', "&quot;")
      .replaceAll("'", "&#39;")
  }
}